import { createContext, useContext, useState, ReactNode } from 'react'
import type { Company } from '@services/companyService'
import type { CompanySummary } from '../types/companySummary'
import type { FileUpload } from '../types/fileUpload'
import { ClientModal } from '@components/ui'
import { useIsPortrait } from '@hooks/useMediaQuery'

export interface ClientModalState {
  isOpen: boolean
  client: Company | CompanySummary | null
  document: FileUpload | null
  onClose: () => void
  onDownloadPdf: () => Promise<void>
  isGeneratingPdf: boolean
  isLoadingClient: boolean
}

const defaultState: ClientModalState = {
  isOpen: false,
  client: null,
  document: null,
  onClose: () => {},
  onDownloadPdf: async () => {},
  isGeneratingPdf: false,
  isLoadingClient: false,
}

const ClientModalContext = createContext<{
  modalState: ClientModalState
  setModalState: (s: ClientModalState) => void
}>({
  modalState: defaultState,
  setModalState: () => {},
})

export function useClientModal() {
  const ctx = useContext(ClientModalContext)
  if (!ctx) throw new Error('useClientModal must be used within ClientModalProvider')
  return ctx
}

function ClientModalLayer() {
  const ctx = useContext(ClientModalContext)
  if (!ctx.modalState.isOpen) return null
  const { modalState } = ctx
  return (
    <ClientModal
      isOpen={modalState.isOpen}
      onClose={modalState.onClose}
      client={modalState.client}
      document={modalState.document}
      onDownloadPdf={modalState.onDownloadPdf}
      isGeneratingPdf={modalState.isGeneratingPdf}
      isLoadingClient={modalState.isLoadingClient}
    />
  )
}

export function ClientModalProvider({ children }: { children: ReactNode }) {
  const [modalState, setModalState] = useState<ClientModalState>(defaultState)
  const isPortrait = useIsPortrait()
  return (
    <ClientModalContext.Provider value={{ modalState, setModalState }}>
      {children}
      {isPortrait && <ClientModalLayer />}
    </ClientModalContext.Provider>
  )
}
